/* @flow */
import { Observable } from 'rxjs';
import { combineEpics } from 'redux-observable';
import { browserHistory } from 'react-router';
import { fromJS } from 'immutable';
import { prop } from 'ramda';
import { querystring } from 'trustar/utils';

import {
  FETCH_ALL,

  FETCH_ANALYSIS,
  FETCH_ANALYSIS_SUCCESS,
  FETCH_ANALYSIS_FAILURE,

  FETCH_ANALYSES_SUCCESS,
  FETCH_ANALYSES_FAILURE,

  FETCH_SHARED_ANALYSES_SUCCESS,
  FETCH_SHARED_ANALYSES_FAILURE,

  SAVE_ANALYSIS,
  SAVE_ANALYSIS_SUCCESS,
  SAVE_ANALYSIS_FAILURE,

  UPDATE_ANALYSIS,
  UPDATE_ANALYSIS_SUCCESS,
  UPDATE_ANALYSIS_FAILURE,

  SHARE_ANALYSIS,
  SHARE_ANALYSIS_SUCCESS,
  SHARE_ANALYSIS_FAILURE,

  DELETE_ANALYSIS,
  DELETE_ANALYSIS_SUCCESS,
  DELETE_ANALYSIS_FAILURE,

} from './action-types.js';
import * as getters from './getters.js';
import { ANALYSIS_ENDPOINT } from './constants.js';
import { toJSON, fromJSON, hasReportNode, analysisToConstellation } from './utils.js';
import { warn, error, success } from 'modules/alerts/actions.js';

/* Fetch saved analyses, triggered on app initialization */
const fetchEpic = (action$, store, { ajax }) =>
  action$.ofType(FETCH_ALL)
    .mergeMap(() => {
      return ajax.get(ANALYSIS_ENDPOINT)
        .map(({ response }) => {
          if (!Array.isArray(response)) {
            throw new Error();
          }
          return {
            type: FETCH_ANALYSES_SUCCESS,
            payload: fromJS(response.map(fromJSON))
          };
        })
        .catch(err => {
          return Observable.from([
            { type: FETCH_ANALYSES_FAILURE, payload: err },
            error('There was an error fetching saved analyses')
          ]);
        });
    });

/* Fetch shared analyses, triggered on app initialization */
const fetchSharedEpic = (action$, store, { ajax }) =>
  action$.ofType(FETCH_ALL)
    .mergeMap(() => {
      return ajax.get(`${ANALYSIS_ENDPOINT}/shared`)
        .map(({ response }) => {
          if (!Array.isArray(response)) {
            throw new Error();
          }
          return {
            type: FETCH_SHARED_ANALYSES_SUCCESS,
            payload: fromJS(response.map(fromJSON))
          };
        })
        .catch(err => {
          return Observable.from([
            { type: FETCH_SHARED_ANALYSES_FAILURE, payload: err },
            error('There was an error fetching shared analyses')
          ]);
        });
    });

/**
 * Fetch a single analysis graph.
 *
 * NOTE: This fetches a GRAPH. Just something to be clear on considering a lot
 * of the other logic in these epics is concerned with case CRUD. This epic is
 * concerned with fetching a graph and thus uses the graph endpoint.
 */
const fetchOneEpic = (action$, store, { ajax }) =>
  action$.ofType(FETCH_ANALYSIS)
    .map(prop('payload'))
    .filter(Boolean) // Don't respond if ID empty
    .mergeMap((id: string) => {
      const url = querystring({ type: 'ANALYSIS', id })('/services/graph'); // See NOTE
      return ajax.get(url)
        .map(({ response }) => analysisToConstellation(response))
        .mergeMap(analysis => {
          const reportNodes = analysis.get('nodes')
            .filter(x => x.get('type') === 'INCIDENT_REPORT');

          /* NOTE: Graphs must contain at least one report node since the id will be
           * used as the id of the graph. We are also now preventing the save of a case
           * with no report nodes, but there may be some saved legacy cases with none. */
          if (reportNodes.count() > 0) {
            return Observable.of({
              type: FETCH_ANALYSIS_SUCCESS,
              payload: { id, data: analysis },
            });
          } else {
            return [
              { type: FETCH_ANALYSIS_FAILURE, payload: { id, data: analysis } },
              warn('Could not display saved analysis because no incident report nodes were found.')
            ];
          }
        })
        .catch(err => Observable.from([
          { type: FETCH_ANALYSIS_FAILURE, payload: err },
          error('There was an error fetching the analysis.')
        ]));
    });

/* Create and save a new analysis from a regular graph */
const createAnalysisEpic = (action$, store, { ajax }) =>
  action$.ofType(SAVE_ANALYSIS)
    .mergeMap(action => Observable.of(action)
      .map(({ payload: { analysis, graph } }) => {
        const title = analysis.get('title');
        const savedAnalyses = getters.saved(store.getState());

        if (savedAnalyses.find(x => x.get('title') === title)) {
          const message = 'You have already saved an analysis with this name. Please select a different name.';
          throw new Error(message);
        }

        /* Alternatively, we could create a way to render cases with no incident report nodes,
         see the fetchOneEpic for details. However, currently such a case will not render at
         all and this is an easy preventative measure for the time being. */
        if (!hasReportNode(graph)) {
          const message = 'You must include at least one incident report node to save a case.';
          throw new Error(message);
        }

        return { analysis, graph };

      })
      .mergeMap(({ analysis, graph }) => {
        return ajax.postJSON(ANALYSIS_ENDPOINT, toJSON(analysis, graph))
          .do(({ response: { guid } }) => browserHistory.push(`/constellation/analysis/${guid}`))
          .mergeMap(({ response }) => {
            return [
              { type: SAVE_ANALYSIS_SUCCESS, payload: fromJSON(response) },
              success('Case saved successfully.')
            ];
          })
          .catch(err => {
            return Observable.from([
              { type: SAVE_ANALYSIS_FAILURE, payload: err },
              error('Case could not be saved.')
            ]);
          });
      })
      .catch((err) => {
        return Observable.from([
          { type: SAVE_ANALYSIS_FAILURE, payload: err },
          error(err.message)
        ]);
      })
    );

/* Update an analysis. */
const updateAnalysisEpic = (action$, store, { ajax }) =>
  action$.ofType(UPDATE_ANALYSIS)
    .mergeMap(({ payload: { analysis, graph } }) => {
      return ajax.postJSON(`${ANALYSIS_ENDPOINT}/${analysis.get('guid')}`, toJSON(analysis, graph))
        .mergeMap(({ response }) => {
          return [
            { type: UPDATE_ANALYSIS_SUCCESS, payload: fromJSON(response) },
            success('Case updated successfully.')
          ];
        })
        .catch(err => {
          return Observable.from([
            { type: UPDATE_ANALYSIS_FAILURE, payload: err },
            error('Case could not be updated.')
          ]);
        });
    });

/* Share an analysis. */
const shareAnalysisEpic = (action$, store, { ajax }) =>
  action$.ofType(SHARE_ANALYSIS)
    .mergeMap(action => Observable.of(action)
      .mergeMap(({ payload: analysis }) => {
        const guid = analysis.get('guid');
        const share = {
          emails: analysis.get('sharedWith').reduce((a, c) => a.concat(c.value), []),
          message: analysis.get('message'),
        };
        return ajax.postJSON(`${ANALYSIS_ENDPOINT}/${guid}/share`, share)
          .mergeMap(({ response }) => {
            if (response && Array.isArray(response)) {
              response = response.join(', ');
            }
            return [
              { type: SHARE_ANALYSIS_SUCCESS, payload: { guid, sharedWith: response }},
              success('Case shared successfully.')
            ];
          })
          .catch(err => {
            return Observable.from([
              { type: SHARE_ANALYSIS_FAILURE, payload: err },
              error('Case could not be shared.')
            ]);
          });
      })
      .catch(err => {
        return Observable.of({ type: SHARE_ANALYSIS_FAILURE, payload: err });
      })
    );

/* Delete an analysis. */
const deleteAnalysisEpic = (action$, store, { ajax }) =>
  action$.ofType(DELETE_ANALYSIS)
    .mergeMap(({ payload: guid }) => {
      return ajax.delete(`${ANALYSIS_ENDPOINT}/${guid}`)
        .mergeMap(({ response }) => {
          return [
            { type: DELETE_ANALYSIS_SUCCESS, payload: guid },
            success('Case deleted successfully.')
          ];
        })
        .catch(err => {
          return Observable.from([
            { type: DELETE_ANALYSIS_FAILURE, payload: err },
            error('Case could not be deleted.')
          ]);
        });
    });

export default combineEpics(
  fetchEpic,
  fetchSharedEpic,
  fetchOneEpic,
  createAnalysisEpic,
  updateAnalysisEpic,
  shareAnalysisEpic,
  deleteAnalysisEpic,
);



// WEBPACK FOOTER //
// ./src/app/modules/analysis/epics.js